import { useMemo, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useLayerSelector } from "../../store/layer";
import { buildWmsLegendUrls, isWmsRasterLayer } from "../../utilities/wmsLegend";

interface LegendEntry {
  id: string;
  urls: string[];
}

/**
 * Floating panel that renders a WMS `GetLegendGraphic` image for each currently
 * visible WMS raster layer. Renders nothing when no WMS layers are on, so it
 * has zero visual footprint on maps that don't use WMS.
 */
const WmsLegendOverlay = () => {
  const { layers, visibleLayers } = useLayerSelector();
  // Default to collapsed so the overlay stays unobtrusive on map load; users opt in
  // to the legend when they need it. Users of maps without WMS layers never see it
  // at all (entries.length === 0 short-circuits render below).
  const [collapsed, setCollapsed] = useState(true);
  // Track images that fail to load so we can silently drop them rather than
  // showing broken-image icons (some WMS servers 404 on GetLegendGraphic).
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());

  const entries = useMemo<LegendEntry[]>(() => {
    return layers
      .filter((layer) => visibleLayers.has(layer.id) && isWmsRasterLayer(layer))
      .map((layer) => ({ id: layer.id, urls: buildWmsLegendUrls(layer) }))
      .filter((entry) => entry.urls.length > 0);
  }, [layers, visibleLayers]);

  if (entries.length === 0) return null;

  const handleImageError = (url: string) => {
    setFailedUrls((prev) => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  };

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: "40px",
        left: "1rem",
        backgroundColor: "#1D2939",
        color: "white",
        border: "1px solid rgba(252, 252, 253, 0.15)",
        borderRadius: "0.5rem",
        padding: "0.5rem 0.75rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        maxWidth: "16rem",
        maxHeight: "60vh",
        overflowY: "auto",
        zIndex: 1
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
        <Typography variant='caption' sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Overlay Legend
        </Typography>
        <IconButton
          size='small'
          onClick={() => setCollapsed((prev) => !prev)}
          aria-label={collapsed ? "Expand overlay legend" : "Collapse overlay legend"}
          sx={{ color: "white", padding: "2px" }}
        >
          {collapsed ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
        </IconButton>
      </Box>

      {!collapsed &&
        entries.map(({ id, urls }) => {
          const usableUrls = urls.filter((url) => !failedUrls.has(url));
          if (usableUrls.length === 0) return null;
          return (
            // `alignItems: flex-start` is load-bearing: without it the flex column's default
            // `stretch` cross-axis alignment stretches each block-level <img> to the full
            // panel width. Combined with `height: auto` that blows small icons (e.g. NAFI's
            // 20x20 hotspot glyphs) up to ~240px tall and blurry. flex-start preserves each
            // image's intrinsic size; `maxWidth: 100%` still caps oversized legends.
            <Box key={id} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.25rem" }}>
              <Typography variant='caption' sx={{ opacity: 0.85 }}>
                {id}
              </Typography>
              {usableUrls.map((url) => (
                <img
                  key={url}
                  src={url}
                  alt={`Legend for ${id}`}
                  onError={() => handleImageError(url)}
                  style={{ maxWidth: "100%", height: "auto", display: "block", imageRendering: "auto" }}
                />
              ))}
            </Box>
          );
        })}
    </Box>
  );
};

export default WmsLegendOverlay;
